import { AppDispatch } from "../../redux/store.ts";
import {
  File,
  Folder,
  FormValue,
  Requirement,
} from "../../redux/interfaces/folder.types.ts";
import { maxAllowedFiles } from "../../components/vault/documents/fixtures/IDTypes.tsx";
import { activeNextPendingRequirement } from "./activeNextPendingRequirement.ts";
import { setActivePanels } from "../../redux/slices/vaultSlice.ts";
import { VaultFormValidationError } from "../../exceptions/VaultFormValidationError.ts";
import { confirmUploadSubmissionBestEffort } from "./resumableUpload.ts";
import { resolveScopedVaultApi } from "../../api/vault.api.ts";
import { getVaultUploadRecoveryContext } from "./vaultActorSession.ts";
import { withVaultUploadRecoveryLease } from "./uploadRecoveryLease.ts";
import { getDocumentUploadPolicy } from "../../constants/uploadPolicies.ts";
import type { UploadPolicy } from "../../constants/uploadPolicies.ts";

interface Params {
  uploadMutateAsync: (params: {
    reqId: string;
    formData: FormData;
    uploadPolicy: UploadPolicy;
    skipStatusUpdate?: boolean;
    uploadBatchAction?: "replace_evidence";
    onResumableFinalized?: (batchId: string) => void;
  }) => Promise<unknown>;
  updateFormValuesMutateAsync: (params: {
    reqId: string;
    form_values: FormValue[] | null;
    status?: string;
    upload_batch_id?: string;
    upload_batch_action?: "replace_evidence_on_submit";
  }) => Promise<any>;
  dispatch: AppDispatch;
  requirementFiles: File[];
  documentType: string;
  uploadPolicyDocumentType?: string;
  formNFiles: number;
  formFiles: any[];
  formValues: { front?: File; back?: File };
  activeRequirement: Requirement | null;
  activeVault: Folder | null;
  activeTab: string | null;
  successMessage: string;
  reqFormValues?: FormValue[];
  messageApi: any;
  status?: "pending_upload" | "pending_review" | "invalid" | "valid";
  skipAutoNavigation?: boolean;
  preserveCurrentEvidence?: boolean;
  minimumUploadFiles?: number;
}

export const uploadFileRequirement = async ({
  uploadMutateAsync,
  updateFormValuesMutateAsync,
  dispatch,
  // requirementFiles,
  documentType,
  uploadPolicyDocumentType,
  formNFiles,
  formFiles,
  formValues: _formValues,
  activeRequirement,
  activeVault,
  activeTab,
  successMessage,
  reqFormValues,
  messageApi,
  status,
  skipAutoNavigation,
  preserveCurrentEvidence = false,
  minimumUploadFiles,
}: Params) => {
  if (formNFiles != maxAllowedFiles[documentType]) {
    const errorMessage = `Por favor sube ${maxAllowedFiles[documentType]} archivo/s.`;
    messageApi.error(errorMessage);
    throw new Error(errorMessage);
  }

  try {
    const { scope: recoveryScope, ownerId } = getVaultUploadRecoveryContext();
    await withVaultUploadRecoveryLease({
      requirementId: activeRequirement!.id,
      recoveryScope,
      ownerId,
      operation: async () => {
        const validFiles = formFiles.filter((file) => file);
        let uploadBatchId: string | undefined;
        if (validFiles.length > 0) {
          const formData = new FormData();
          validFiles.forEach((file: any) => {
            const toUpload = file?.originFileObj || file;
            if (toUpload) {
              formData.append("files", toUpload);
            }
          });
          const selectedDocumentType =
            uploadPolicyDocumentType ||
            reqFormValues?.find(
              (formValue) => formValue.key === "document_type",
            )?.value;
          if (selectedDocumentType) {
            formData.append("document_type", selectedDocumentType);
          }
          await uploadMutateAsync({
            reqId: activeRequirement!.id,
            formData,
            uploadPolicy: {
              ...getDocumentUploadPolicy({
                requirementSlug: activeRequirement?.slug,
                documentType: selectedDocumentType,
                requirementStatus: activeRequirement?.status,
              }),
              ...(minimumUploadFiles !== undefined
                ? { min_files: Math.max(minimumUploadFiles, 1) }
                : {}),
            },
            skipStatusUpdate: true,
            onResumableFinalized: (batchId) => {
              uploadBatchId = batchId;
            },
          });
        }

        // Update form values (default to pending_review for file uploads)
        await updateFormValuesMutateAsync({
          reqId: activeRequirement!.id,
          form_values: reqFormValues || activeRequirement?.form_values || null,
          status: status ?? "pending_review",
          upload_batch_id: uploadBatchId,
          ...(uploadBatchId && !preserveCurrentEvidence
            ? { upload_batch_action: "replace_evidence_on_submit" as const }
            : {}),
        });
        if (uploadBatchId) {
          const uploadMutationApi = resolveScopedVaultApi(
            typeof window !== "undefined" ? window.location.pathname : "",
          );
          await confirmUploadSubmissionBestEffort(
            uploadMutationApi,
            activeRequirement!.id,
            uploadBatchId,
            undefined,
            recoveryScope,
          );
        }

        dispatch(setActivePanels([]));

        if (!skipAutoNavigation) {
          const requirements =
            activeVault?.subfolders
              ?.filter((subfolder) => subfolder.id === activeTab)
              .map((subfolder) => subfolder.requirements)
              .flat() || [];
          activeNextPendingRequirement({
            dispatch,
            activeRequirement,
            requirements,
          });
        }

        messageApi.success(successMessage);
      },
    });
  } catch (error: unknown) {
    // Backend field-level rejection (RV001) after the files already
    // uploaded: name the fields and reasons instead of a generic error, so
    // the user knows the documents persisted but the metadata was rejected.
    if (error instanceof VaultFormValidationError) {
      const labelByKey = new Map(
        (reqFormValues || activeRequirement?.form_values || []).map((f) => [
          f.key,
          f.label || f.key,
        ]),
      );
      const reasons = Object.entries(error.fields)
        .slice(0, 3)
        .map(([key, message]) => `${labelByKey.get(key) ?? key}: ${message}`)
        .join(" · ");
      const errorMessage = reasons
        ? `${error.message} ${reasons}`
        : error.message;
      messageApi.error(errorMessage);
      throw new Error(errorMessage);
    }
    const errorMessage = error instanceof Error ? error.message : String(error);
    messageApi.error(errorMessage);
    throw new Error(errorMessage);
  }
};
